// --- Dependencies -------------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import constants from "@/config/constants";
import type { TyApiResponse } from "@/types";
import type { TyResourceOptions, TyResourceTable } from "./types";

// --- Constants ----------------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 25;

// --- Logic --------------------------------------------------------------------------------------

/** Drops empty values so a cleared filter leaves the query string rather than sending `key=`. */
function compact(query: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === "") continue;
        out[key] = value;
    }
    return out;
}

/**
 * Everything the six list screens do identically: fetch a page, hold the selection, and write through
 * the model's bulk endpoints. A screen brings its columns, its filters and its stats — nothing else.
 *
 * Writes all go through `PUT {path}/bulk`, including a single inline cell edit, which is simply a bulk
 * of one. That keeps one validation path per model on the server instead of a second single-row route
 * that would drift from it, and it means the table and the bulk bar can never disagree about what a
 * field is allowed to hold.
 *
 * `useFetch` cannot carry these calls: it re-runs on `invoke` rather than on argument change and owns a
 * single result slot, so a write firing through it would race the list request and overwrite the rows
 * with the write's own response. The list keeps `useFetch`; writes use `fetch` directly and then ask the
 * list to refetch.
 */
export default function useResourceTable<T, S = Record<string, number>>({
    path,
    rowsKey,
    query,
    pageSize = DEFAULT_PAGE_SIZE,
}: TyResourceOptions): TyResourceTable<T, S> {
    const t = useTranslation();
    const token = useAuth((state) => state.token);
    const lang = useLang((state) => state.lang);

    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [writeError, setWriteError] = useState<string | null>(null);

    const list = useFetch<TyApiResponse<Record<string, unknown>>>(path, { method: "GET" }, false);

    /**
     * Serialised first, then parsed back. The screens rebuild their query object on every render, so
     * depending on the object itself would refetch on every keystroke anywhere on the page — the string
     * is what actually changed when a filter changed, and it is the only dependency worth watching.
     */
    const queryKey = JSON.stringify(compact(query));
    const activeQuery = useMemo(() => JSON.parse(queryKey) as Record<string, unknown>, [queryKey]);

    /** A narrowed list is a different list — page 4 of the old one is meaningless against the new one. */
    useEffect(() => {
        setPage(1);
        setSelected([]);
    }, [activeQuery]);

    useEffect(() => {
        list.invoke({ query: { ...activeQuery, page, limit: pageSize } });
    }, [activeQuery, page, pageSize]);

    const refetch = useCallback(() => {
        list.invoke({ query: { ...activeQuery, page, limit: pageSize } });
    }, [activeQuery, page, pageSize]);

    const body = list.data?.data;
    const rows = (body?.[rowsKey] as T[]) ?? [];
    const total = (body?.total as number) ?? 0;
    const stats = (body?.stats as S) ?? null;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));

    /**
     * One writer for every mutation on these screens. Returns a boolean rather than throwing, because
     * every caller's job on failure is the same: put the old value back and show why.
     */
    const write = useCallback(
        async (method: "PUT" | "DELETE" | "POST", data: Record<string, unknown>, endpoint = "bulk"): Promise<boolean> => {
            setBusy(true);
            setWriteError(null);

            try {
                const response = await fetch(`${constants.url}/${path}/${endpoint}`, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        "x-language": lang ?? "ar",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(data),
                });

                const json = await response.json().catch(() => null);

                if (!response.ok || !json?.success) {
                    setWriteError(json?.message ?? t.errors.unknown);
                    return false;
                }

                refetch();
                return true;
            } catch {
                setWriteError(t.errors.network);
                return false;
            } finally {
                setBusy(false);
            }
        },
        [path, token, lang, refetch, t]
    );

    const saveCell = useCallback((id: string, key: string, value: unknown) => write("PUT", { ids: [id], set: { [key]: value } }), [write]);

    const applyBulk = useCallback(
        async (set: Record<string, unknown>) => {
            const ok = await write("PUT", { ids: selected, set });
            if (ok) setSelected([]);
            return ok;
        },
        [write, selected]
    );

    /**
     * A row's own endpoint rather than the bulk one — for the handful of actions that are a decision
     * and not an edit, like activating a paid subscription. Same error handling, same refetch.
     */
    const runAction = useCallback(
        (id: string, action: string, data: Record<string, unknown> = {}) => write("POST", data, `${id}/${action}`),
        [write]
    );

    const deleteBulk = useCallback(
        async (ids?: string[]) => {
            const ok = await write("DELETE", { ids: ids ?? selected });
            if (ok) setSelected([]);
            return ok;
        },
        [write, selected]
    );

    return {
        rows,
        stats,
        total,
        page,
        setPage,
        lastPage,
        loading: list.loading,
        error: list.error,
        refetch,
        selected,
        setSelected,
        busy,
        writeError,
        saveCell,
        applyBulk,
        deleteBulk,
        runAction,
    };
}
